import React, { useContext, useEffect, useState } from "react";
import "./premiumAccessCard.css";
import { DataLayerContext } from "../../dataLayer/DataLayer";
import { useObservableProperty } from "../../observable/useObservableProperty";
import { TierStar } from "./tierStar";
import { nativeStore, periodLabel, StorePlan } from "../../storeBilling";
import { isAppleStorePreview } from "../../testMode";
import { SubscriptionChooser } from "./subscriptionChooser";
import { recordDiagnosticEvent } from "../../diagnostics";

const tierDescriptions = {
  bronze: {
    introduction:
      "Support Wirder's development and ongoing hosting costs, and get access to extra features in the game!",
    star: "Lovely Bronze Star",
    recognition: "Your friends on the leaderboard will see that you support this game!",
  },
  silver: {
    introduction:
      "Show your enthusiastic support for the game! Help with Wirder's development and ongoing hosting costs, and enjoy extra features in the game.",
    star: "Beautiful Silver Star",
    recognition: "Your friends on the leaderboard will see that you really value this game!",
  },
  gold: {
    introduction:
      "Love the game and feeling super generous? Support Wirder's development and ongoing hosting costs, and enjoy extra features in the game.",
    star: "Snazzy Gold Star",
    recognition:
      "Your friends on the leaderboard will see that you're a top supporter of this game!",
  },
};

const previewPlans: StorePlan[] = [
  {
    productId: "wirder.bronze.monthly",
    tier: "bronze",
    title: "Bronze",
    price: "$0.99",
    period: "P1M",
  },
  {
    productId: "wirder.silver.monthly",
    tier: "silver",
    title: "Silver",
    price: "$2.99",
    period: "P1M",
  },
  { productId: "wirder.gold.monthly", tier: "gold", title: "Gold", price: "$9.99", period: "P1M" },
];

const previewNotice =
  "UI preview only. No Apple payment sheet is opened and no purchase is made. Real sandbox checkout must be tested in TestFlight.";

function tierLabel(tier: string): string {
  return `${tier[0].toUpperCase()}${tier.slice(1)}`;
}

export function PremiumAccessCard({ context }: { context: "leaderboard" | "stats" | "settings" }) {
  const dataLayer = useContext(DataLayerContext);
  const premium = useObservableProperty(dataLayer.premium);
  const storeRecoveryError = useObservableProperty(dataLayer.storeRecoveryError);
  const [plans, setPlans] = useState<StorePlan[] | null>(null);
  const [selected, setSelected] = useState(0);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [resetConfirming, setResetConfirming] = useState(false);
  const preview = isAppleStorePreview();
  const store = preview ? "apple" : nativeStore();
  const storeName = store === "apple" ? "Apple App Store" : "Google Play";
  const sandbox = premium.billingEnvironment === "Sandbox";
  const canReset =
    context === "settings" && !preview && store === "apple" && sandbox &&
    premium.status === "ready" && !premium.error &&
    premium.subscription?.environment === "Sandbox";

  useEffect(() => {
    if (!canReset) setResetConfirming(false);
  }, [canReset]);

  useEffect(() => {
    if (premium.status === "ready" && premium.subscription) setChooserOpen(false);
  }, [premium.status, premium.subscription]);

  useEffect(() => {
    if (premium.status === "idle") void dataLayer.loadPremiumStatus();
  }, [dataLayer, premium.status]);

  const run = async (action: () => Promise<string>) => {
    setBusy(true);
    setMessage("");
    try {
      setMessage(await action());
    } catch (error) {
      console.error("Premium subscription action failed");
      setMessage(
        error instanceof Error ? error.message : "Subscription action failed. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const loadPlans = () =>
    run(async () => {
      setPlans(preview ? previewPlans : (await dataLayer.loadStorePlans()).plans);
      setSelected(0);
      setChooserOpen(true);
      return "";
    });
  const refresh = () =>
    run(async () => {
      const result = await dataLayer.forcePremiumAccessRefresh();
      if (result === "error") throw new Error("Could not verify premium access. Please try again.");
      return result === "active"
        ? "Your premium access is up to date."
        : "No active subscription was found for this Wirder account.";
    });

  if (premium.tier && context !== "settings") return null;
  if (!chooserOpen && (premium.status === "idle" || premium.status === "loading")) {
    return (
      <section className="premium-card premium-card--loading" role="status" aria-busy="true">
        Checking premium access&hellip;
      </section>
    );
  }
  const management =
    premium.subscription?.store === "apple"
      ? "https://apps.apple.com/account/subscriptions"
      : premium.subscription?.store === "google"
        ? "https://play.google.com/store/account/subscriptions"
        : null;

  return (
    <section
      className={`premium-card${
        premium.tier ? ` premium-card--active premium-card--${premium.tier}` : ""
      }`}
      aria-busy={busy}
    >
      <div className="premium-card__head">
        <span className="premium-card__mark">
          <TierStar tier={premium.tier} variant="card" />
        </span>
        <div>
          <div className="premium-card__eyebrow">
            {premium.tier ? `${tierLabel(premium.tier)} supporter` : "Wirder premium"}
          </div>
          <div className="premium-card__title">
            {premium.tier ? "Premium extras unlocked" : "Support Wirder"}
          </div>
        </div>
      </div>
      <p className="premium-card__copy">
        {premium.tier
          ? premium.source === "complimentary"
            ? `You have complimentary ${tierLabel(premium.tier)} access.`
            : sandbox
              ? "Premium access is active in this test build. Sandbox purchases do not grant paid access on the website or released app."
              : `Your ${tierLabel(
                  premium.tier,
                )} membership is active on this Wirder account, including on the web.`
          : "Subscriptions help pay for the work behind Wirder while the game stays free and ad-free for everyone. Subscribers receive a few extra features as a thank you."}
      </p>
      {preview && (
        <p className="premium-card__linked" role="status">
          iOS UI preview — sample USD prices, not live store offers. {previewNotice}
        </p>
      )}
      {premium.error && (
        <p className="premium-card__linked" role="status">
          We couldn&rsquo;t refresh your premium access. Please check again.
        </p>
      )}
      {context === "settings" && storeRecoveryError && (
        <p className="premium-card__linked" role="status">
          Your store purchases couldn&rsquo;t be checked. Please try Restore Purchases.
        </p>
      )}
      {!premium.tier && !store && (
        <p className="premium-card__linked">
          Subscribe in the Wirder iOS app, then sign in here with the same Wirder account. Already
          subscribed? Check access below.
        </p>
      )}
      <div className="premium-card__actions">
        {management && sandbox ? (
          <button
            className="btn btn--primary premium-card__button"
            disabled={busy}
            onClick={() =>
              run(() =>
                preview ? Promise.resolve(previewNotice) : dataLayer.manageStoreSubscriptions(),
              )
            }
          >
            Manage Test Subscription
          </button>
        ) : management ? (
          <a
            className="btn btn--primary premium-card__button"
            href={management}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              recordDiagnosticEvent("subscription-management", {
                stage: "opened-external",
                store: premium.subscription?.store,
                billingEnvironment: premium.billingEnvironment,
              })
            }
          >
            Manage Subscription
          </a>
        ) : (
          store &&
          (!premium.tier || sandbox) && (
            <>
              <button
                className="btn btn--primary premium-card__button"
                disabled={busy}
                onClick={loadPlans}
              >
                {busy ? "Loading subscriptions\u2026" : "View subscriptions"}
              </button>
              {chooserOpen && plans && (
                <SubscriptionChooser busy={busy} onClose={() => setChooserOpen(false)}>
                  <div className="subscription-chooser__body">
                    {plans[selected] && (
                      <div
                        className="subscription-chooser__description"
                        aria-live="polite"
                        aria-atomic="true"
                      >
                        <p>{tierDescriptions[plans[selected].tier].introduction}</p>
                        <ul>
                          <li>
                            <strong>{tierDescriptions[plans[selected].tier].star}:</strong>{" "}
                            {tierDescriptions[plans[selected].tier].recognition}
                          </li>
                          <li>
                            <strong>Leaderboard:</strong> Invite your friends to your leaderboard
                            and see each other's scores every day.
                          </li>
                          <li>
                            <strong>Extra Stats:</strong> See your personal records by word length,
                            such as the most seven-letter words you've found in a day.
                          </li>
                        </ul>
                      </div>
                    )}
                    <fieldset className="premium-card__plans" disabled={busy}>
                      <legend>Choose your support level</legend>
                      {plans.map((plan, index) => (
                        <label
                          className="premium-card__plan"
                          key={`${plan.productId}:${plan.basePlanId ?? ""}`}
                        >
                          <input
                            type="radio"
                            name={`premium-plan-${context}`}
                            checked={selected === index}
                            onChange={() => setSelected(index)}
                          />
                          <TierStar tier={plan.tier} variant="name" />
                          <span>
                            <strong>{tierLabel(plan.tier)}</strong> &mdash; {plan.price}{" "}
                            {periodLabel(plan.period)}
                          </span>
                        </label>
                      ))}
                    </fieldset>
                    {preview && (
                      <p className="premium-card__linked">
                        {previewNotice} Prices shown are US samples.
                      </p>
                    )}
                    <p className="premium-card__account-link">
                      <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
                        Privacy
                      </a>
                      {" \u00b7 "}
                      <a
                        href={
                          store === "apple"
                            ? "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                            : "/terms.html"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Terms
                      </a>
                    </p>
                    {message && (
                      <div className="premium-card__status" role="status">
                        {message}
                      </div>
                    )}
                  </div>
                  <div className="subscription-chooser__footer">
                    <button
                      className="btn btn--primary premium-card__button"
                      disabled={busy || !plans[selected]}
                      onClick={() =>
                        run(() =>
                          preview
                            ? Promise.resolve(previewNotice)
                            : dataLayer.purchaseStorePlan(plans[selected]),
                        )
                      }
                    >
                      {busy ? "Please wait\u2026" : `Subscribe with ${storeName}`}
                    </button>
                  </div>
                </SubscriptionChooser>
              )}
            </>
          )
        )}
        <button
          className="premium-card__text-button"
          disabled={busy}
          onClick={
            store
              ? () =>
                  run(() =>
                    preview ? Promise.resolve(previewNotice) : dataLayer.restoreStorePurchases(),
                  )
              : refresh
          }
        >
          {busy ? "Please wait\u2026" : "Restore Purchases"}
        </button>
        {canReset && !resetConfirming && (
          <button
            className="premium-card__text-button"
            disabled={busy}
            onClick={() => {
              setMessage("");
              setResetConfirming(true);
            }}
          >
            Reset Test Subscription
          </button>
        )}
        {canReset && resetConfirming && (
          <div role="group" aria-label="Confirm test subscription reset">
            <p className="premium-card__copy">
              Remove test subscription access from this Wirder account? This does not cancel
              Apple subscriptions or erase game history. Clear the sandbox tester&rsquo;s Apple
              purchase history before buying again.
            </p>
            <div className="premium-card__actions">
              <button
                className="btn btn--primary premium-card__button"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const result = await dataLayer.resetTestSubscription();
                    setResetConfirming(false);
                    return result;
                  })
                }
              >
                {busy ? "Resetting\u2026" : "Reset test access"}
              </button>
              <button
                className="premium-card__text-button"
                disabled={busy}
                onClick={() => setResetConfirming(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {message && !chooserOpen && (
        <div className="premium-card__status" role="status" aria-live="polite">
          {message}
        </div>
      )}
    </section>
  );
}
